import { Branding, DisabledPrompt, Link, useBCMSModal, useParams, useStateManager, useUIState, useWorkflowStep, BrandingStyle } from "bcms-react";
import { FormRowView, TableColumnContainer, TableColumnView, TableHeaderView, TableItemTextView, TableItemView, TableView } from "components/shared";
import { TableBody } from "components/shared/views/TableView";
import { getDefaultSearch, getWorkflowSteps } from "components/workflows/DepositHistoryWorkflow";
import React, { useState } from "react";
import { ImageAsset, SVGAsset } from "res";
import CustomTableAccordion, { CustomTableContent, CustomTableToggle, useCustomTableAccordion } from "../../../../custom-ui/CustomTableAccordion";
import "./styles.css";

/**
 * @function DepositHistoryView
 * @param {BCMSDate} date - This is the date the deposit was made.
 * @param {STRING} accountNumber - This is the account number the deposit was made to.
 * @param {STRING} accountName - The name of the account the deposit was made to.
 * @param {STRING} status - The status of the deposit. 
 * @param {DECIMAL} amount - The amount of the deposit.
 * @param {STRING} transactionID - The transaction ID of each deposit.
 * @param {STRING} source - This is the source of where the transaction was made.
 * @returns {React.Component}
 * @exported true
 * @description This sets up the table for the Deposit History page.
 * @example
 * <DepositHistoryView date="12/12/12" accountNumber="x1234" accountName="Reward Checking" status="Pending" amount="$12.34" transactionID="9863" source="Mobile Phone" />
 */
export default function DepositHistoryView(props) {
  const { data, ui } = useStateManager();

  const filtered = data.deposits;
  const isSearching = ui.isSearching;

  const depositRows = filtered.map((deposit, index) => {
    var account = deposit.account;
    return <DepositHistoryAccordion key={deposit.id} date={deposit.date.format("m/d/Y")} accountName={account.nickname} accountNumber={account.number}
     status={deposit.status} amount={deposit.amount.format()} transactionID={deposit.transactionID} source={deposit.source} depositID={deposit.id}/>
  }, []);

  return (
    <div className="show-on-desktop">
    { filtered.length > 0 && 
    <div className="container-section">
      <TableView className="deposit-history-accordion-view">
        <TableColumnContainer>
          <TableColumnView />
          <TableColumnView text="Date" className="text-left" />
          <TableColumnView text={
            <div className="flex-container align-center">
              <p className="set-width">Account</p>
              <DisabledPrompt>
                <SVGAsset id="SortIcon" className="icon clickable" />
              </DisabledPrompt>
            </div>
            }>
          </TableColumnView>
          <TableColumnView text={
            <div className="flex-container align-center">
              <p className="set-width">Status</p>
              <DisabledPrompt>
                <SVGAsset id="SortIcon" className="icon clickable" />
              </DisabledPrompt>
            </div>
            }>
          </TableColumnView>
          <TableColumnView text={
            <div className="flex-container align-center justify-right">
              <p className="set-width">Amount</p>
              <DisabledPrompt>
                <SVGAsset id="SortIcon" className="icon clickable" />
              </DisabledPrompt>
            </div>
            }>
          </TableColumnView>
        </TableColumnContainer>
        <TableBody>
          <TableHeaderView text="Processed" colSpan="5" />
        </TableBody>
        {depositRows}
        {/*<DepositHistoryAccordion date={date} accountName={accountName} accountNumber={accountNumber} status={status} amount={amount} transactionID={transactionID} source={source} />*/}
      </TableView>
    </div>
    }
    {/* The No Search Results component needs to be hidden until the user searched for a deposit and the search does not match any deposits */}
    { filtered.length === 0 && <DepositHistoryNoResults /> }
    { !isSearching &&
    <EndOfDepositsWidget />
    }
    </div>
  );
}

/**
 * @function DepositHistoryAccordion
 * @param {BCMSDate} date - This is the date the deposit was made.
 * @param {STRING} accountNumber - This is the account number the deposit was made to.
 * @param {STRING} accountName - The name of the account the deposit was made to.
 * @param {STRING} status - The status of the deposit. 
 * @param {DECIMAL} amount - The amount of the deposit.
 * @param {STRING} transactionID - The transaction ID of each deposit.
 * @param {STRING} source - This is the source of where the transaction was made.
 * @returns {React.Component}
 * @exported false
 * @description This is the accordion for each deposit. It has the DepositHistoryRow and the DepositHistoryAccordionView.
 * @example
 * <DepositHistoryAccordion date="12/12/12" accountNumber="x1234" accountName="Reward Checking" status="Pending" amount="$12.34" transactionID="9863" source="Mobile Phone" />
 */
function DepositHistoryAccordion(props) {
  const uiStateConfig = getDepositHistoryItemConfig(props.depositID); 
  const { config, key } = useUIState(uiStateConfig.key, uiStateConfig.value);

  const date = props.date;
  const accountNumber = props.accountNumber;
  const accountName = props.accountName;
  const status = props.status;
  const amount = props.amount;
  const transactionID = props.transactionID;
  const source = props.source;
  const depositID = props.depositID;

  return (
    <CustomTableAccordion activeClass="opened" state={ config.accordionActive }>
      <DepositHistoryRow date={date} accountName={accountName} accountNumber={accountNumber} status={status} amount={amount} depositID={depositID} config={ config } uiKey={ key } />
      <DepositHistoryAccordionView transactionID={transactionID} source={source} depositID={depositID} config={ config } uiKey={ key } />
    </CustomTableAccordion>
  );
}

export const getDepositHistoryItemConfig = id => ({
  key: `DepositItem_${ id }`,
  value: {
    accordionActive: false,
    showChecks: false
  }
});

/**
 * @function DepositHistoryRow
 * @param {BCMSDate} date - This is the date the deposit was made.
 * @param {STRING} accountNumber - This is the account number the deposit was made to.
 * @param {STRING} accountName - The name of the account the deposit was made to.
 * @param {STRING} status - The status of the deposit. 
 * @param {DECIMAL} amount - The amount of the deposit.
 * @returns {React.Component}
 * @exported false
 * @description This is the row that goes in the table for each deposit history.
 * @example
 * <DepositHistoryRow date="12/12/12" accountNumber="x1234" accontName="Reward Checking" status="Pending" amount="$12.34" />
 */
function DepositHistoryRow(props) {
  const config = props.config;
  const key = props.uiKey;

  const { onItemClick } = useStateManager();
  const toggleClick = value => onItemClick(key, "accordionActive", !config.accordionActive);

  const date = props.date;
  const accountNumber = props.accountNumber;
  const accountName = props.accountName;
  const status = props.status;
  const amount = props.amount;
  const depositID = props.depositID;

  return (
    <CustomTableToggle onClick={ toggleClick }>
      <tr className="table-item-container">
        <TableItemView className="expand-row-chevron text-center">
          <SVGAsset id={ config.accordionActive ? "DownChevronIcon" : "RightChevronIcon"} className="icon" data-point={`deposit_Chevron_${depositID}`}/>
        </TableItemView>

        <TableItemTextView className="text-left" text={date} dataPoint={`deposit_date_${depositID}`}/>
        <TableItemTextView className="text-left" text={accountName + " - " + accountNumber} dataPoint={`deposit_account_${depositID}`}/>
        <TableItemTextView className="text-left" text={status} dataPoint={`deposit_status_${depositID}`}/>
        <TableItemTextView className="text-right" text={amount} dataPoint={`deposit_amount_${depositID}`}/>
      </tr>
    </CustomTableToggle>
  )
}

/**
 * @function DepositHistoryAccordionView
 * @param {STRING} transactionID - The transaction ID of each deposit.
 * @param {STRING} source - This is the source of where the transaction was made.
 * @param {FUNCTION} onClick - Allows us to set an event handler for the button when clicked.
 * @returns {React.Component}
 * @exported false
 * @description This is the accordion details for each deposit history row. It shows the checks and more detailed info for each deposit.
 * @example
 * <DepositHistoryAccordionView transactionID="234" source="Mobile Phone" />
 */
function DepositHistoryAccordionView(props) {
  const { onItemClick } = useStateManager();

  const config = props.config;
  const key = props.uiKey;

  const toggleClick = value => onItemClick(key, "accordionActive", !config.accordionActive);
  const checkToggle = evt => onItemClick(key, "showChecks", !config.showChecks);

  const depositID = props.depositID;
  //const transactionID = props.transactionID ?? "[XXX]";
  // temporary solution since creating new deposits would increment the transaction id.
  const transactionID = "XXXX";
  const source = props.source ?? "[source]";
  const { open } = useBCMSModal();

  const onCheckImageClick = prop => val => {
    open("CheckImagePreviewModal", { view: prop });
  };

  return (
    <CustomTableContent>
      <tr className="custom-transaction-content table-item-container">
        <td colSpan="6">
          <div className="flex-container flex-horizontal details-row">
            <p className="flex-width">DEPOSIT DETAILS</p>
            <CustomTableToggle onClick={ toggleClick }>
              <SVGAsset id="CloseX" className="clickable" data-point={`Close_Deposit_${depositID}`}/>
            </CustomTableToggle>
          </div>
          <FormRowView>
          <Branding id="view_check_images_abi22" value="true">
            <div className="flex-container flex-horizontal view-check">
                <BrandingStyle className="clickable link" style={{ "color": "links_abi22" }} element={
                  <span onClick={checkToggle} data-point={`Toggle_Deposit_Check_${depositID}`}>{config.showChecks ? "Hide" : "View"} Check</span>
                } />
            </div>
            </Branding>
          </FormRowView>
          
          {config.showChecks &&
            <>
              <div className="flex-container flex-horizontal check-row">
                <ImageAsset id="CheckFront" className="flex-width clickable" data-point={`Front_Deposit_Check_${depositID}`} onClick={ onCheckImageClick("front") } />
                <ImageAsset id="CheckBack" className="flex-width clickable" data-point={`Back_Deposit_Check_${depositID}`} onClick={ onCheckImageClick("back") } />
              </div>
            </>}
          <FormRowView text="Transaction ID">
            <p><b>{transactionID}</b></p>
          </FormRowView>
          <FormRowView text="Source">
            <p><b>{source}</b></p>
          </FormRowView>
        </td>
      </tr>
    </CustomTableContent>
  );
}

/**
 * @function DepositHistorySearchCriteria
 * @param {BCMSDate} dateRange - This is the dateRange is the selected date range the user is searching within.
 * @param {STRING} accountNumber - This is the account number the deposit was made to.
 * @param {STRING} accountName - The name of the account the deposit was made to.
 * @param {STRING} status - The status of the deposit. 
 * @param {DECIMAL} amount - The amount of the deposit.
 * @returns {React.Component}
 * @exported true
 * @description This shows what criteria the search results are based off of.
 * @example
 * <DepositHistorySearchCriteria accountNUmber="x1234" accountName="Reward Checking" dateRange="Last 120 Days" amount="$12.32" />
 */
/*export function DepositHistorySearchCriteria(props) {
  const accountNumber = props.accountNumber;
  const accountName = props.accountName;
  const dateRange = props.dateRange;
  const amount = props.amount;

  return (
    <div className="flex-container deposit-history-search-criteria">
      <b className="set-width">Search Criteria:&nbsp;</b>
      <p className="flex-width">
        Account: {accountName + " - " + accountNumber};
        Date Range: {dateRange};
        Amount: {amount};
      </p>
      <Link className="set-width">Clear Search</Link>
    </div>
  )
}*/

/**
 * @function DepositHistoryNoResults
 * @returns {React.Component}
 * @exported true
 * @description This is shown when there aren't any deposits that match the users entered search criteria.
 * @example
 * <DepositHistoryNoResults />
 */
export function DepositHistoryNoResults(props) {
  return (
    <div className="text-center deposit-history-no-results">
      <p>No deposits match your search criteria.</p>
    </div>
  )
}

 /**
 * @function EndOfDepositsWidget
 * @return {React.Component}
 * @exported false
 * @description Shown in "DepositHistoryView". Currently statically shown.
 * @example 
 * <EndOfDepositsWidget />
 */
export function EndOfDepositsWidget(props) {
  return (
    <div className="end-of-transactions-message flex-container">
      <SVGAsset id="InfoIcon" className="info-icon set-width" />
      <p className="flex-width">End of recent deposits. Please use the Search feature to see older deposits.</p>
    </div>
  );
}
